// RecipeFormModal.jsx - Updated with prefixed CSS classes
import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import './RecipeFormModal.css';

const RecipeFormModal = ({ recipe, menuItems, ingredients, onClose, onSave, isLoading }) => {
  const [formData, setFormData] = useState({ ...recipe });
  const [recipeIngredients, setRecipeIngredients] = useState(recipe.ingredients || []);
  const [selectedIngredient, setSelectedIngredient] = useState('');
  const [ingredientQuantity, setIngredientQuantity] = useState('');
  const [ingredientNotes, setIngredientNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  
  // Calculate total cost
  const totalCost = recipeIngredients.reduce((sum, item) => {
    // Ensure we're working with numbers
    const itemCost = typeof item.cost === 'number' 
      ? item.cost 
      : item.quantity * (parseFloat(item.ingredient?.costPerUnit) || 0);
    
    return sum + itemCost;
  }, 0);
  
  // Get selected menu item details
  const selectedMenuItemDetails = menuItems.find(
    item => item.id?.toString() === formData.menuItemId?.toString()
  );
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Hata mesajlarını temizle
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  // Mevcut reçeteyi düzenlerken verileri yükle
  useEffect(() => {
    if (recipe) {
      console.log("Reçete verileri yükleniyor:", recipe);
      
      // Eğer recipe.menuItem varsa ve menuItemId yoksa, menuItemId ekle
      if (recipe.menuItem && !recipe.menuItemId) {
        const updatedRecipe = {
          ...recipe,
          menuItemId: recipe.menuItem.id
        };
        setFormData(updatedRecipe);
      } else {
        setFormData(recipe);
      }
      
      // Malzemeleri ayarla
      if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
        setRecipeIngredients(recipe.ingredients);
      }
    }
  }, [recipe]);
  
  // Add ingredient to recipe
  const handleAddIngredient = () => {
    if (!selectedIngredient || !ingredientQuantity) {
      setErrors({
        ...errors,
        ingredient: !selectedIngredient ? 'Malzeme seçmelisiniz' : '',
        quantity: !ingredientQuantity ? 'Miktar girmelisiniz' : ''
      });
      return;
    }
    
    const ingredient = ingredients.find(i => i.id.toString() === selectedIngredient);
    
    if (!ingredient) {
      return;
    }
    
    // Check if ingredient already exists
    const exists = recipeIngredients.some(
      item => item.ingredient?.id?.toString() === selectedIngredient
    );
    
    if (exists) {
      setErrors({
        ...errors,
        ingredient: 'Bu malzeme zaten eklenmiş'
      });
      return;
    }
    
    // Parse values safely
    const quantity = parseFloat(ingredientQuantity);
    const costPerUnit = parseFloat(ingredient.costPerUnit) || 0;
    const totalCost = quantity * costPerUnit;
    
    const newIngredient = {
      ingredient: {
        id: ingredient.id,
        name: ingredient.name,
        unit: ingredient.unit,
        costPerUnit: costPerUnit
      },
      ingredientId: ingredient.id, // API için gerekli
      quantity: quantity,
      notes: ingredientNotes,
      cost: totalCost
    };
    
    setRecipeIngredients([...recipeIngredients, newIngredient]);
    setSelectedIngredient('');
    setIngredientQuantity('');
    setIngredientNotes('');
    setErrors({ ...errors, ingredient: '', quantity: '' });
  };
  
  // Remove ingredient from recipe
  const handleRemoveIngredient = (ingredientId) => {
    setRecipeIngredients(
      recipeIngredients.filter(item => 
        (item.ingredient?.id?.toString() !== ingredientId?.toString()) && 
        (item.ingredientId?.toString() !== ingredientId?.toString())
      )
    );
  };
  
  // Save recipe
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    
    // Düzenleme modunda menuItem kontrolünü atla (düzenleme sırasında menuItem değiştirilemez)
    if (!formData.menuItemId && !recipe.id) {
      newErrors.menuItemId = 'Menü öğesi seçmelisiniz';
    }
    
    if (recipeIngredients.length === 0) {
      newErrors.ingredients = 'En az bir malzeme eklemelisiniz';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    console.log("Form verileri:", formData);
    console.log("Malzemeler:", recipeIngredients);
    
    // Prepare data for submission
    const submissionData = {
      ...formData,
      // Eğer düzenleme modundaysa ve menuItemId yoksa, menuItem.id'yi kullan
      menuItemId: formData.menuItemId || (formData.menuItem ? formData.menuItem.id : ''),
      ingredients: recipeIngredients.map(item => ({
        ingredientId: item.ingredientId || item.ingredient?.id,
        quantity: parseFloat(item.quantity) || 0,
        notes: item.notes || null
      })),
      // Boş değerleri null'a dönüştür veya sayısal değerleri parse et
      preparationTime: formData.preparationTime ? parseInt(formData.preparationTime) : null,
      costPerServing: formData.costPerServing ? parseFloat(formData.costPerServing) : null,
      description: formData.description || null,
      preparationInstructions: formData.preparationInstructions || null,
      portionSize: formData.portionSize || null,
      // ID kontrolü - eğer varsa string'e çevir
      id: formData.id ? formData.id.toString() : undefined
    };
    
    console.log("API'ye gönderilecek veri:", submissionData);
    onSave(submissionData);
  };
  
  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isLoading]);
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  
  // Debug
  useEffect(() => {
    console.log("Modal açıldı, gelen veri:", recipe);
    console.log("Mevcut ingredients:", recipeIngredients);
  }, []);
  
  return (
    <div className="rcp-modal-overlay" onClick={isLoading ? null : onClose}>
      <div className="rcp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rcp-modal-header">
          <h2>{recipe.id ? 'Reçete Düzenle' : 'Yeni Reçete Oluştur'}</h2>
          <button 
            className="rcp-modal-close-button"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Kapat"
          >
            ×
          </button>
        </div>
        
        <div className="rcp-modal-tabs">
          <div 
            className={`rcp-tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            Temel Bilgiler
          </div>
          <div 
            className={`rcp-tab ${activeTab === 'instructions' ? 'active' : ''}`}
            onClick={() => setActiveTab('instructions')}
          >
            Hazırlama Talimatları
          </div>
          <div 
            className={`rcp-tab ${activeTab === 'ingredients' ? 'active' : ''}`}
            onClick={() => setActiveTab('ingredients')}
          >
            Malzemeler
          </div>
          <div 
            className={`rcp-tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            Özet
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="rcp-form">
          <div className="rcp-modal-body">
            {errors.submit && <div className="rcp-error-message">{errors.submit}</div>}
            
            {activeTab === 'basic' && (
              <div className="rcp-tab-content">
                <div className="rcp-form-group">
                  <label htmlFor="menuItemId">Menü Öğesi</label>
                  <select
                    id="menuItemId"
                    name="menuItemId"
                    value={formData.menuItemId || (formData.menuItem ? formData.menuItem.id : '')}
                    onChange={handleInputChange}
                    className={errors.menuItemId ? 'rcp-has-error' : ''}
                    disabled={isLoading || !!recipe.id}
                  >
                    <option value="">Menü Öğesi Seçin</option>
                    {menuItems.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name} - ₺{parseFloat(item.price).toFixed(2)}
                      </option>
                    ))}
                  </select>
                  {errors.menuItemId && <div className="rcp-error-text">{errors.menuItemId}</div>}
                </div>
                
                <div className="rcp-form-group">
                  <label htmlFor="description">Açıklama</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    rows="2"
                    disabled={isLoading}
                    placeholder="Reçete hakkında kısa bir açıklama yazın (opsiyonel)"
                  ></textarea>
                </div>
                
                <div className="rcp-form-row">
                  <div className="rcp-form-group half">
                    <label htmlFor="portionSize">Porsiyon Miktarı</label>
                    <input
                      id="portionSize"
                      type="text"
                      name="portionSize"
                      value={formData.portionSize || ''}
                      onChange={handleInputChange}
                      placeholder="Örn: 1 kişilik, 250 gr"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div className="rcp-form-group half">
                    <label htmlFor="preparationTime">Hazırlama Süresi (Dakika)</label>
                    <input
                      id="preparationTime"
                      type="number"
                      name="preparationTime"
                      value={formData.preparationTime || ''}
                      onChange={handleInputChange}
                      min="0"
                      disabled={isLoading}
                      placeholder="Hazırlama süresi"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'instructions' && (
              <div className="rcp-tab-content">
                <div className="rcp-form-group">
                  <label htmlFor="preparationInstructions">Hazırlama Talimatları</label>
                  <textarea
                    id="preparationInstructions"
                    name="preparationInstructions"
                    value={formData.preparationInstructions || ''}
                    onChange={handleInputChange}
                    rows="12"
                    placeholder="Reçete hazırlama adımlarını yazın..."
                    disabled={isLoading}
                  ></textarea>
                </div>
              </div>
            )}
            
            {activeTab === 'ingredients' && (
              <div className="rcp-tab-content">
                <div className="rcp-ingredient-form">
                  <h3>Malzeme Ekle</h3>
                  <div className="rcp-form-row">
                    <div className="rcp-form-group half">
                      <label htmlFor="ingredient">Malzeme</label>
                      <select
                        id="ingredient"
                        value={selectedIngredient}
                        onChange={(e) => setSelectedIngredient(e.target.value)}
                        className={errors.ingredient ? 'rcp-has-error' : ''}
                        disabled={isLoading}
                      >
                        <option value="">Malzeme Seçin</option>
                        {ingredients.map(ingredient => {
                          // Malzemenin eklenip eklenmediğini kontrol et
                          const isAdded = recipeIngredients.some(
                            item => (item.ingredient?.id?.toString() === ingredient.id?.toString()) ||
                                   (item.ingredientId?.toString() === ingredient.id?.toString())
                          );
                          
                          return (
                            <option 
                              key={ingredient.id} 
                              value={ingredient.id}
                              disabled={isAdded}
                            >
                              {ingredient.name} ({ingredient.unit})
                            </option>
                          );
                        })}
                      </select>
                      {errors.ingredient && <div className="rcp-error-text">{errors.ingredient}</div>}
                    </div>
                    
                    <div className="rcp-form-group quarter">
                      <label htmlFor="quantity">Miktar</label>
                      <input
                        id="quantity"
                        type="number"
                        value={ingredientQuantity}
                        onChange={(e) => setIngredientQuantity(e.target.value)}
                        min="0"
                        step="0.01"
                        placeholder="Miktar"
                        className={errors.quantity ? 'rcp-has-error' : ''}
                        disabled={isLoading}
                      />
                      {errors.quantity && <div className="rcp-error-text">{errors.quantity}</div>}
                    </div>
                    
                    <div className="rcp-form-group quarter">
                      <label htmlFor="notes">Notlar</label>
                      <input
                        id="notes"
                        type="text"
                        value={ingredientNotes}
                        onChange={(e) => setIngredientNotes(e.target.value)}
                        placeholder="Opsiyonel"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  
                  <button 
                    type="button"
                    className="rcp-add-ingredient-button"
                    onClick={handleAddIngredient}
                    disabled={isLoading}
                  >
                    <FaPlus className="rcp-button-icon" />
                    Malzeme Ekle
                  </button>
                </div>
                
                <hr className="rcp-divider" />
                
                <div className="rcp-ingredients-list">
                  <h3>Reçete Malzemeleri</h3>
                  {errors.ingredients && (
                    <div className="rcp-error-message small">{errors.ingredients}</div>
                  )}
                  
                  {recipeIngredients.length > 0 ? (
                    <div className="rcp-ingredients-table-container">
                      <table className="rcp-ingredients-table">
                        <thead>
                          <tr>
                            <th>Malzeme</th>
                            <th>Miktar</th>
                            <th>Birim Fiyat</th>
                            <th>Toplam</th>
                            <th>Notlar</th>
                            <th>İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                        {recipeIngredients.map((item, index) => {
                          // Malzeme ismi için güvenli erişim
                          const ingredientName = item.ingredient?.name || 
                            ingredients.find(ing => ing.id?.toString() === item.ingredientId?.toString())?.name || 
                            'Bilinmeyen Malzeme';
                          
                          // Birim için güvenli erişim
                          const unit = item.ingredient?.unit || 
                            ingredients.find(ing => ing.id?.toString() === item.ingredientId?.toString())?.unit || 
                            'birim';
                          
                          // Birim fiyat için güvenli erişim
                          const costPerUnit = parseFloat(item.ingredient?.costPerUnit) || 
                            parseFloat(ingredients.find(ing => ing.id?.toString() === item.ingredientId?.toString())?.costPerUnit) || 
                            0;
                          
                          // Toplam maliyet hesapla
                          const cost = typeof item.cost === 'number' 
                            ? item.cost 
                            : item.quantity * costPerUnit;
                          
                          return (
                            <tr key={index}>
                              <td>{ingredientName}</td>
                              <td>
                                {item.quantity} {unit}
                              </td>
                              <td>
                                ₺{costPerUnit.toFixed(2)}
                              </td>
                              <td>
                                ₺{cost.toFixed(2)}
                              </td>
                              <td>{item.notes || '-'}</td>
                              <td>
                                <button
                                  type="button"
                                  className="rcp-remove-ingredient-button"
                                  onClick={() => handleRemoveIngredient(item.ingredient?.id || item.ingredientId)}
                                  disabled={isLoading}
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <th colSpan="3" className="rcp-text-right">
                              Toplam Maliyet:
                            </th>
                            <th>₺{totalCost.toFixed(2)}</th>
                            <th colSpan="2"></th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="rcp-info-message small">
                      Henüz malzeme eklenmemiş
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'summary' && (
              <div className="rcp-tab-content">
                <div className="rcp-summary-section">
                  <div className="rcp-summary-cards">
                    <div className="rcp-summary-card">
                      <h3 className="rcp-summary-card-header">Reçete Bilgileri</h3>
                      <div className="rcp-summary-card-content">
                        <div className="rcp-summary-item">
                          <span className="rcp-summary-label">Menü Öğesi:</span>
                          <span className="rcp-summary-value">{selectedMenuItemDetails?.name || 'Seçilmemiş'}</span>
                        </div>
                        <div className="rcp-summary-item">
                          <span className="rcp-summary-label">Kategori:</span>
                          <span className="rcp-summary-value">{selectedMenuItemDetails?.category?.name || 'Kategorisiz'}</span>
                        </div>
                        <div className="rcp-summary-item">
                          <span className="rcp-summary-label">Porsiyon:</span>
                          <span className="rcp-summary-value">{formData.portionSize || '-'}</span>
                        </div>
                        <div className="rcp-summary-item">
                          <span className="rcp-summary-label">Hazırlama Süresi:</span>
                          <span className="rcp-summary-value">{formData.preparationTime ? `${formData.preparationTime} dakika` : '-'}</span>
                        </div>
                        <div className="rcp-summary-item">
                          <span className="rcp-summary-label">Malzeme Sayısı:</span>
                          <span className="rcp-summary-value">{recipeIngredients.length}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="rcp-summary-card">
                      <h3 className="rcp-summary-card-header">Maliyet ve Fiyat Analizi</h3>
                      <div className="rcp-summary-card-content">
                        <div className="rcp-summary-item">
                          <span className="rcp-summary-label">Toplam Maliyet:</span>
                          <span className="rcp-summary-value">₺{totalCost.toFixed(2)}</span>
                        </div>
                        <div className="rcp-summary-item">
                          <span className="rcp-summary-label">Satış Fiyatı:</span>
                          <span className="rcp-summary-value">
                            ₺{parseFloat(selectedMenuItemDetails?.price || 0).toFixed(2)}
                          </span>
                        </div>
                        {selectedMenuItemDetails && (
                          <>
                            <div className="rcp-summary-item">
                              <span className="rcp-summary-label">Kar Marjı:</span>
                              <span className="rcp-summary-value rcp-profit-value">
                                ₺{(parseFloat(selectedMenuItemDetails.price || 0) - totalCost).toFixed(2)}
                              </span>
                            </div>
                            <div className="rcp-summary-item">
                              <span className="rcp-summary-label">Kar Yüzdesi:</span>
                              <span className="rcp-summary-value rcp-profit-value">
                                %{
                                  parseFloat(selectedMenuItemDetails.price || 0) > 0 
                                    ? (((parseFloat(selectedMenuItemDetails.price || 0) - totalCost) / parseFloat(selectedMenuItemDetails.price || 0)) * 100).toFixed(2)
                                    : '0.00'
                                }
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {formData.preparationInstructions && (
                    <div className="rcp-preparation-instructions">
                      <h3>Hazırlama Talimatları</h3>
                      <p className="rcp-instructions-text">{formData.preparationInstructions}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="rcp-modal-footer">
            <button 
              type="button" 
              className="rcp-cancel-button"
              onClick={onClose}
              disabled={isLoading}
            >
              İptal
            </button>
            <button 
              type="submit"
              className="rcp-save-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="rcp-button-spinner"></div>
                  <span>Kaydediliyor...</span>
                </>
              ) : (
                'Kaydet'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipeFormModal;